import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQueries } from 'react-query';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { RemoteAccCenterRepo } from '@repository/acc/center/AccCenterRepo';
import { useInsightUnreadResume } from '@repository/insight/useInsight';
import { RemoteInsightRepo } from '@repository/insight/InsightRepo';
import AdPopup from './adPopup';
import AccNeedAdditionalModal from './accNeedAdditionalModal';
import PasswordSecureModal from './PasswordSecureModal';
import MatchingProposalModal from './MatchProposalModal';
import ResumeProfileNoticeModal from './resumeProfileNoticeModal';
import UpdatePreferModal from './updatePreferModal';
import { ContentRouteUrl, RouteUrl } from '@utils/routeUtil';
import useModal from '@utils/hooks/useModal';
import useLocalStorageExpires from '@utils/hooks/useLocalStorageExpires';

export interface AppContextModalComponentProps {
  contextOpenModal: () => void;
}
/**
 * 팝업 순서(필수)
 * 1.15세 미만이라면 생년월일 입력 페이지로 이동.
 * 2.장기 미접속자라면 장기 해제 페이지로 이동.
 * 3.비밀번호 변경 90일이면 모달 노출.
 *
 *
 * 팝업 규칙
 * 필수가 있으면 선택은 노출되지 않는다.
 * 모달은 1개만 노출된다.
 */

const resumeProfileNoticeModalQueryKey = {
  queryKey: ['insight', 'unread', 'resume'],
  queryFn: async () => new RemoteInsightRepo().fetchInsightUnreadResume,
};

const accNeedAdditionalModalQueryKey = {
  queryKey: ['acca', 'center', 'recent'],
  queryFn: async () => new RemoteAccCenterRepo().fetchAccCenterApplyRecent,
};

const useQueryIsLoading = () => {
  const { data: userInfo, isLoading } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const modalQueries = isLogin ? [resumeProfileNoticeModalQueryKey, accNeedAdditionalModalQueryKey] : [];
  const queryResults = useQueries(modalQueries);
  const isAllQueiresLoading = queryResults.some((query) => query.isLoading);

  return isLoading ? true : isAllQueiresLoading;
};

export const ResumeProfileNoticeModalExpiresKey = 'resumeProfileNoticeModalExpires';

export const useRequiredPopup = () => {
  const router = useRouter();
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const requiredPopupItem = ['USER_15_TEEN', 'LONG_TERM_INACTIVE_VERIFICATION_REQUIRED'];
  const visibleRequiredPopup = userInfo?.authorities?.some((authority) => requiredPopupItem.includes(authority));
  const visiblePasswordSecureModal =
    !visibleRequiredPopup && userInfo?.authorities?.includes('PASSWORD_CHANGE_REQUIRED');

  const { getIsExpired } = useLocalStorageExpires();
  const isModalExpired = getIsExpired(ResumeProfileNoticeModalExpiresKey);
  const { data: unreadInsightResume } = useInsightUnreadResume(isLogin);
  const visibleResumeProfileNoticeModalPath = [RouteUrl.INDEX, RouteUrl.PROFILE, RouteUrl.MATCH].includes(
    router.pathname as any,
  );
  const visibleResumeProfileNoticeModal =
    !visiblePasswordSecureModal && visibleResumeProfileNoticeModalPath && !!unreadInsightResume && isModalExpired;

  useEffect(() => {
    if (!userInfo || !router.isReady) return;

    if (userInfo.authorities?.includes('USER_15_TEEN')) {
      if (router.pathname !== ContentRouteUrl.UPDATE_BIRTHDATE) router.push(ContentRouteUrl.UPDATE_BIRTHDATE);
      return;
    }

    if (userInfo.authorities?.includes('LONG_TERM_INACTIVE_VERIFICATION_REQUIRED')) {
      if (router.pathname !== ContentRouteUrl.UPDATE_ACCOUNT) router.push(ContentRouteUrl.UPDATE_ACCOUNT);
      return;
    }
  }, [userInfo, router]);

  return { visibleRequiredPopup, visiblePasswordSecureModal, visibleResumeProfileNoticeModal };
};

const AppContextModalComponent = () => {
  const { pathname } = useRouter();
  const apiLoading = useQueryIsLoading();
  const { visibleRequiredPopup, visiblePasswordSecureModal, visibleResumeProfileNoticeModal } = useRequiredPopup();

  //AdPopup노출 조건확인을 위한 state
  const { openModal, isModalOpen, closeModal } = useModal();

  useEffect(() => {
    closeModal();
  }, [pathname]);

  if (visibleRequiredPopup || apiLoading) return null;

  return (
    <>
      {/* 전역에서 뜨는 모달 */}
      <PasswordSecureModal isVisible={visiblePasswordSecureModal} />

      {/* INDEX, PROFILE, MATCH에서 노출 */}
      <ResumeProfileNoticeModal isVisible={visibleResumeProfileNoticeModal} type={'type2'} />

      {/* 선택모달 - 아래 모달들은 겹치는 경우가 없기 때문에 열린 걸 알 필요가 없어서 저렇게 처리 */}

      {/*PROFILE, MATCH, ACC_RESULTS에서 노출 */}
      {!visiblePasswordSecureModal && !visibleResumeProfileNoticeModal && (
        <MatchingProposalModal contextOpenModal={openModal} />
      )}
      {/* ACCA_TEST_GATE에서 노출 */}
      {!visiblePasswordSecureModal && <AccNeedAdditionalModal contextOpenModal={openModal} />}

      {/* INDEX페이지에 노출 */}
      {!visiblePasswordSecureModal && !visibleResumeProfileNoticeModal && (
        <UpdatePreferModal contextOpenModal={openModal} />
      )}

      {!visiblePasswordSecureModal && !visibleResumeProfileNoticeModal && !isModalOpen && <AdPopup />}
    </>
  );
};

export default AppContextModalComponent;
